import { AxiosCalls } from "../Functions/AxiosCalls";
import {
  ILoginFormData,
  ILoggedInUserData,
  IChangePasswordFormData,
  IHandleChangePasswordParams,
  IForgotPasswordRequestFormData,
  IHandleForgotPasswordRequestParams,
  IHandleResetPasswordConfirmParams,
  IResetPasswordConfirmBody,
  ISignupResponse,
  ISignupFormData,
  IHandleSignupParams,
  DecodedUser,
  IHandleLogoutParams,
  IHandleResendVerificationParams,
  IHandleVerifyCodeParams,
  IHandleEditProfileParams,
  IAuthenticatedUser,
} from "@model/Authentication.model";
import { IHandleLoginParams } from "@model/Authentication.model";
import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

export class AuthenticationService {
  private navigate = useNavigate();

  async handleLogin({
    data,
    reset,
    setLoginFailedCount,
    handleUserData,
    setApiError,
    setLoading,
  }: IHandleLoginParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<ILoggedInUserData, ILoginFormData>(
        "/auth/login",
        data,
        ""
      );

      const decodedUser: DecodedUser | null = this.decodeToken(
        resp.data.accessToken
      );
      handleUserData({
        token: resp.data.accessToken,
        user: {
          firstName: decodedUser.firstName,
          lastName: decodedUser.lastName,
          email: decodedUser.email,
          role: decodedUser.role,
          phoneNumber: decodedUser.phoneNumber,
        },
      });
      setLoginFailedCount(0);
      reset();
      return resp.data.accessToken;
    } catch (err) {
      setLoginFailedCount((n) => n + 1);
      const msg = handleDefaultApiErrors(err);
      setApiError(msg);
      handleUserData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async logout({
    setApiError,
    setLoading,
    handleUserData,
    token,
    isProtectedRoute = true,
  }: IHandleLogoutParams): Promise<boolean> {
    setLoading(true);
    setApiError(null);

    try {
      if (!token) {
        setApiError("You're not logged in.");
        return false;
      }

      await AxiosCalls.Post<{ message: string }, Record<string, never>>(
        "/auth/logout",
        {},
        token
      );

      handleUserData(null);
      if (isProtectedRoute) {
        this.navigate("/auth/login", { replace: true });
      }

      return true;
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      return false;
    } finally {
      setLoading(false);
    }
  }

  async handlePasswordResetRequest({
    data,
    reset,
    setApiError,
    setLoading,
  }: IHandleForgotPasswordRequestParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<
        { message: string },
        IForgotPasswordRequestFormData
      >("/auth/password-reset/request", data, "");

      if (reset) reset({ email: "" });
      this.navigate(`/auth/reset-password/${encodeURIComponent(data.email)}`, {
        replace: true,
      });
      return resp?.data?.message ?? "A new verification code has been sent.";
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleChangePassword({
    data,
    reset,
    setApiError,
    setLoading,
    handleUserData,
    token,
    onAuthError
  }: IHandleChangePasswordParams): Promise<boolean> {
    setLoading(true);
    setApiError(null);

    try {
      await AxiosCalls.Post<void, IChangePasswordFormData>(
        "/auth/change-password",
        data,
        token
      );

      reset();

      await this.logout({
        setApiError,
        setLoading,
        handleUserData,
        token,
        isProtectedRoute: true,
      });

      return true;
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError
      });
      setApiError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }
  async handleSignup({
    data,
    reset,
    setApiError,
    setLoading,
    verifyUser,
  }: IHandleSignupParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<ISignupResponse, ISignupFormData>(
        "/auth/signup",
        data,
        ""
      );

      const param = new URLSearchParams({
        email: resp.data.email,
        isNew: "true",
      });

      reset();
      verifyUser({
        verification: {
          email: resp.data.email,
          isNew: false,
        },
      });
      this.navigate(`/auth/verification`, {
        replace: true,
      });
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
    } finally {
      setLoading(false);
    }
  }

  async handlePasswordResetConfirm({
    data,
    reset,
    setApiError,
    setLoading,
  }: IHandleResetPasswordConfirmParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);
    try {
      const resp = await AxiosCalls.Post<
        { message: string },
        IResetPasswordConfirmBody
      >("/auth/password-reset/confirm", data, "");

      reset();
      this.navigate("/auth/login", { replace: true });
      return resp?.data?.message ?? "Password changed successfully!";
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      return null;
    } finally {
      setLoading(false);
    }
  }

  decodeToken(token: string): DecodedUser {
    try {
      return jwtDecode<DecodedUser>(token);
    } catch (error) {
      console.error("Invalid token", error);
      throw error;
    }
  }

  async handleVerifyCode({
    code,
    setApiError,
    setLoading,
  }: IHandleVerifyCodeParams): Promise<boolean> {
    setLoading(true);
    setApiError(null);

    try {
      await AxiosCalls.Get<void>(
        `/auth/verify/${encodeURIComponent(code)}`,
        ""
      );

      this.navigate("/auth/login", { replace: true });
      return true;
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      return false;
    } finally {
      setLoading(false);
    }
  }
  async handleResendVerification({
    email,
    isNew = false,
    navigate = true,
    setApiError,
    setLoading,
    navigateTo,
  }: IHandleResendVerificationParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<
        { message?: string },
        { email: string }
      >("/auth/verification/resend", { email }, "");

      if (navigate) {
        const url = `/auth/verification?email=${encodeURIComponent(
          email
        )}&isNew=${String(isNew)}`;
        if (navigateTo) navigateTo(url, { replace: true });
        else this.navigate(url, { replace: true });
      }

      return resp?.data?.message ?? "A new verification code has been sent.";
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleEditProfile({
    data,
    token,
    setApiError,
    setLoading,
    handleUserData,
    reset,
    onAuthError
  }: IHandleEditProfileParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        userType: data.accountType === "reseller" ? "RESELLER" : "CLIENT",
      };

      const resp = await AxiosCalls.Put<{ accessToken: string }, typeof payload>(
        "/auth/update-profile",
        payload,
        token
      );

      const decoded: DecodedUser = this.decodeToken(resp.data.accessToken);

      const updatedUser: IAuthenticatedUser = {
        token: resp.data.accessToken,
        user: {
          firstName: decoded.firstName,
          lastName: decoded.lastName,
          email: decoded.email,
          phoneNumber: decoded.phoneNumber,
          role: decoded.role,
        },
      };

      handleUserData(updatedUser);

      reset({
        firstName: updatedUser.user.firstName,
        lastName: updatedUser.user.lastName,
        email: updatedUser.user.email,
        phoneNumber: updatedUser.user.phoneNumber,
        accountType:
          updatedUser.user.role === "RESELLER" ? "reseller" : "client",
      });

      return "Profile updated successfully";
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError
      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }


    getSafeReturnPath(params: {
    bannerReturnPath: string | null;
    preservedReturnPath: string;
  }): string {
    const { bannerReturnPath, preservedReturnPath } = params;

    if (
      bannerReturnPath &&
      !bannerReturnPath.startsWith("/auth") &&
      bannerReturnPath !== "/"
    ) {
      return bannerReturnPath;
    }

    return preservedReturnPath;
  }
}
